/**
 * Phase 5 batch 3 Slice 1 — Firebase SDK initialization for the
 * applicant-web.
 *
 * Reads runtime config from Vite's `import.meta.env` (variables prefixed
 * with `VITE_FIREBASE_*`). The values are NOT secret — Firebase web
 * config is public per the official guidance — but we keep them out of
 * source so the same build can target multiple Firebase projects.
 *
 * In tests the SDK is fully mocked at the import boundary (see
 * `tests/setup.ts`), so this module is never actually executed in vitest.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/firebase/app/dist/index.d.ts`):
 *   - `initializeApp(config)` -> FirebaseApp
 *   - `getAuth(app)` -> Auth
 *   - `getFirestore(app)` -> Firestore
 *   - `getStorage(app)` -> FirebaseStorage
 *
 * https://firebase.google.com/docs/web/setup
 */

import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import {
  type AppCheck,
  initializeAppCheck,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { type Auth, getAuth } from 'firebase/auth';
import { type Firestore, getFirestore } from 'firebase/firestore';
import { type FirebaseStorage, getStorage } from 'firebase/storage';

export interface IFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * App Check (Phase 6a Slice 2) — optional client config. The site
 * key is provisioned in the Firebase Console under "App Check →
 * Apps → Web (reCAPTCHA v3)" and pasted into
 * `VITE_FIREBASE_APPCHECK_SITE_KEY`. When the env var is missing we
 * skip App Check init so local-dev builds without the key still
 * boot — production deploys MUST set it once App Check enforcement
 * is turned on Console-side.
 *
 * In dev mode we honour the SDK's debug-token contract: setting
 * `self.FIREBASE_APPCHECK_DEBUG_TOKEN = true` before
 * `initializeAppCheck` makes the SDK print a debug token to the
 * console; the user registers that token in the App Check Console
 * so localhost can mint attestations without a reCAPTCHA challenge.
 *
 * Reference: https://firebase.google.com/docs/app-check/web/debug-provider
 */
export interface IAppCheckConfig {
  /** reCAPTCHA v3 site key from the App Check Console. */
  siteKey: string;
  /** When true (typically only in `import.meta.env.DEV`), enables the
   *  debug-token provider before init. Default: false. */
  useDebugToken?: boolean;
}

/**
 * Reads `VITE_FIREBASE_*` env vars and returns a fully-formed config.
 * Throws a single, actionable error listing every missing key when any
 * are absent so a misconfigured deployment fails loud instead of
 * "succeeding" with broken auth.
 */
export function readFirebaseConfigFromEnv(): IFirebaseConfig {
  const env = (import.meta as unknown as { env: Record<string, string> }).env;
  const required: Record<keyof IFirebaseConfig, string> = {
    apiKey: 'VITE_FIREBASE_API_KEY',
    authDomain: 'VITE_FIREBASE_AUTH_DOMAIN',
    projectId: 'VITE_FIREBASE_PROJECT_ID',
    storageBucket: 'VITE_FIREBASE_STORAGE_BUCKET',
    messagingSenderId: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
    appId: 'VITE_FIREBASE_APP_ID',
  };
  const missing: string[] = [];
  const out: Partial<IFirebaseConfig> = {};
  for (const [field, key] of Object.entries(required) as Array<
    [keyof IFirebaseConfig, string]
  >) {
    const value = env?.[key];
    if (typeof value !== 'string' || value.length === 0) {
      missing.push(key);
    } else {
      out[field] = value;
    }
  }
  if (missing.length > 0) {
    throw new Error(
      `Firebase web config is missing required env vars: ${missing.join(', ')}.\n` +
        `Copy apps/applicant-web/.env.example to apps/applicant-web/.env.local ` +
        `and fill in the values from the Firebase Console.`,
    );
  }
  return out as IFirebaseConfig;
}

export interface IFirebaseRuntime {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
  /**
   * App Check handle. `null` when no site key is configured (e.g.,
   * a development build without `VITE_FIREBASE_APPCHECK_SITE_KEY`).
   */
  appCheck: AppCheck | null;
}

let cached: IFirebaseRuntime | null = null;

/**
 * Result shape for {@link tryGetFirebaseRuntime}. Lets the UI decide
 * what to do when Firebase config is missing (typical in fresh
 * checkouts before the `.env.local` file has been populated): show a
 * "Firebase not configured" banner and run the demo in localStorage-
 * only mode rather than crash with a blank page.
 */
export type FirebaseRuntimeResult =
  | { ok: true; runtime: IFirebaseRuntime }
  | { ok: false; error: Error };

/**
 * Non-throwing variant of {@link getFirebaseRuntime}. Returns
 * `{ ok: false, error }` when the env vars are missing instead of
 * throwing. Use this from the React entry point so a misconfigured
 * dev environment falls back to the localStorage-backed demo path
 * (see Phase 2 batch 3 Slice 5) instead of rendering an empty page.
 *
 * Production deploys MUST have the env vars set; the FirebaseApp
 * wrapper surfaces a visible banner when the runtime is missing so
 * the misconfiguration can't be missed.
 */
export function tryGetFirebaseRuntime(
  configOverride?: IFirebaseConfig,
  appCheckOverride?: IAppCheckConfig | null,
): FirebaseRuntimeResult {
  try {
    return {
      ok: true,
      runtime: getFirebaseRuntime(configOverride, appCheckOverride),
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Reads the optional App Check config from env. Returns `null` when
 * the site key is not configured so the caller can skip App Check
 * init without crashing the app.
 *
 * The debug-token branch only fires when `import.meta.env.DEV` is
 * true; in production builds Vite tree-shakes the dev-only code path.
 */
export function readAppCheckConfigFromEnv(): IAppCheckConfig | null {
  const env = (import.meta as unknown as {
    env: Record<string, string | boolean | undefined>;
  }).env;
  const siteKey = env?.['VITE_FIREBASE_APPCHECK_SITE_KEY'];
  if (typeof siteKey !== 'string' || siteKey.length === 0) {
    return null;
  }
  return {
    siteKey,
    useDebugToken: env?.['DEV'] === true,
  };
}

/**
 * Initializes (idempotently) the Firebase SDK and returns handles to
 * Auth, Firestore, Storage, and App Check. Subsequent calls return
 * the same runtime so the modules-of-modules pattern (init in one
 * place, use everywhere) works without re-initializing.
 *
 * App Check is wired AFTER `initializeApp` and BEFORE any
 * Firestore/Storage call so attestations attach to every outgoing
 * request — that ordering is required by the Firebase docs:
 *   https://firebase.google.com/docs/app-check/web/recaptcha-provider#initialize
 */
export function getFirebaseRuntime(
  configOverride?: IFirebaseConfig,
  appCheckOverride?: IAppCheckConfig | null,
): IFirebaseRuntime {
  if (cached !== null) return cached;
  const config = configOverride ?? readFirebaseConfigFromEnv();
  // `getApps()` lets us avoid re-initialization if HMR re-runs this
  // module — Firebase throws on duplicate `initializeApp` calls.
  const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config);

  // App Check init (Phase 6a Slice 2). The override seam exists so
  // tests can pass `null` to skip App Check entirely without the
  // module reading `import.meta.env`.
  const appCheckConfig =
    appCheckOverride !== undefined
      ? appCheckOverride
      : readAppCheckConfigFromEnv();
  let appCheck: AppCheck | null = null;
  if (appCheckConfig !== null) {
    if (appCheckConfig.useDebugToken === true) {
      // The debug-token provider is opt-in: the SDK reads
      // `self.FIREBASE_APPCHECK_DEBUG_TOKEN` only when set BEFORE
      // `initializeAppCheck`. In dev, setting it to `true` makes
      // the SDK log a fresh token to the console; the user pastes
      // that token into the App Check Console to register the
      // localhost build.
      (
        globalThis as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }
      ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckConfig.siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  const runtime: IFirebaseRuntime = {
    app,
    auth: getAuth(app),
    firestore: getFirestore(app),
    storage: getStorage(app),
    appCheck,
  };
  cached = runtime;
  return runtime;
}

/** Test seam — resets the cached runtime so unit tests can rebind. */
export function __resetFirebaseRuntimeForTests(): void {
  cached = null;
}
